import { useEffect, useState } from 'react';
import Reveal from '../../components/Reveal';
import HeroSlideshow from '../../components/HeroSlideshow';
import SafeImage from '../../components/SafeImage';
import './tarangire.css';
import {
  IMAGE_FALLBACK,
  TARANGIRE_HERO_SLIDES,
  TARANGIRE_PAGE_IMAGES,
} from '../../data/destinationImages';

import elephantFace from '../../assets/images/elephant-face.jpg';
import lionFace from '../../assets/images/lion-face.jpg';
import wildebeestFace from '../../assets/images/wildebeest-face.jpg';
import giraffeOverview from '../../assets/images/hero-giraffe.jpg';
import wilddogFace from '../../assets/images/wilddog-face.jpg';
import eagleFace from '../../assets/images/eagle-face.jpg';

const TARANGIRE_IMAGES = TARANGIRE_PAGE_IMAGES;

const HERO_STATS = [
  { value: '2,850', label: 'km² of wilderness', delay: 0 },
  { value: '3,000+', label: 'Elephants (dry season)', delay: 80 },
  { value: '550+', label: 'Bird species', delay: 160 },
  { value: '1970', label: 'Established', delay: 240 },
];

const WILDLIFE = [
  {
    animalFace: elephantFace,
    title: 'Elephant Herds',
    text: 'Tarangire holds one of Tanzania\'s greatest elephant concentrations  massive herds gather along the river in the dry season.',
    facePosition: 'center 42%',
  },
  {
    animalFace: lionFace,
    title: 'Lions & Leopards',
    text: 'Predators stalk the riverine woodlands and open plains lions are commonly seen, with leopards in acacia thickets.',
    facePosition: 'center 38%',
  },
  {
    animalFace: wildebeestFace,
    title: 'Buffalo & Zebras',
    text: 'Large buffalo herds and zebra columns move between grasslands and water  classic East African savanna drama.',
    facePosition: 'center 35%',
  },
  {
    animalFace: giraffeOverview,
    title: 'Giraffes & Kudu',
    text: 'Maasai giraffes and lesser kudu browse among baobabs and commiphora woodland on the park\'s quieter tracks.',
    facePosition: 'center 30%',
  },
  {
    animalFace: wilddogFace,
    title: 'Pythons & Wild Dogs',
    text: 'Giant pythons drape from acacia branches while endangered wild dogs hunt the open bush rare but unforgettable sightings.',
    facePosition: 'center 36%',
  },
  {
    animalFace: eagleFace,
    title: 'Birdlife',
    text: 'Over 550 species including hornbills, lovebirds, ostriches, and raptors  a birder\'s paradise year-round.',
    facePosition: 'center 28%',
  },
];

const SEASONS = [
  {
    badge: 'peak',
    title: 'Dry Season',
    months: 'June – October',
    text: 'Wildlife crowds the Tarangire River as water sources elsewhere dry up  peak elephant viewing and predator action.',
  },
  {
    badge: 'green',
    title: 'Green Season',
    months: 'November – March',
    text: 'Lush landscapes, calving season, and excellent birding fewer vehicles and dramatic baobab silhouettes at sunset.',
  },
  {
    badge: 'shoulder',
    title: 'Shoulder Season',
    months: 'April – May',
    text: 'Long rains transform the park into emerald grassland. Wildlife disperses but rates drop and mornings stay clear.',
  },
];

const EXPERIENCES = [
  {
    image: TARANGIRE_IMAGES.river,
    title: 'Tarangire River Drive',
    text: 'Follow the park\'s lifeline where elephants, giraffes, and antelope gather the defining dry-season safari experience.',
  },
  {
    image: TARANGIRE_IMAGES.baobabs,
    title: 'Baobab Woodlands',
    text: 'Wind through ancient baobab groves and golden savanna  landscapes unlike anywhere else on the Northern Circuit.',
  },
  {
    image: TARANGIRE_IMAGES.savanna,
    title: 'Northern Circuit Safari',
    text: 'Pair Tarangire with Lake Manyara, Ngorongoro, and the Serengeti for Tanzania\'s classic multi-park adventure.',
  },
];

const GALLERY_IMAGES = [
  { src: TARANGIRE_IMAGES.hero, caption: 'Tarangire National Park' },
  { src: TARANGIRE_IMAGES.wildlife, caption: 'Savanna Wildlife' },
  { src: TARANGIRE_IMAGES.river, caption: 'Tarangire River' },
  { src: TARANGIRE_IMAGES.baobabs, caption: 'Baobab Woodlands' },
  { src: TARANGIRE_IMAGES.overview, caption: 'Plains & Herds' },
  { src: TARANGIRE_IMAGES.savanna, caption: 'Golden Savanna' },
];

const SAFARI_PACKAGES = [
  {
    image: TARANGIRE_IMAGES.river,
    title: 'Tarangire Day Safari',
    duration: '1 day',
    tag: 'Day trip',
    text: 'A full day from Arusha along the Tarangire River elephants, baobabs, and classic dry-season game viewing.',
    highlights: ['Arusha pickup', 'Full-day game drive', 'Picnic lunch'],
  },
  {
    image: TARANGIRE_IMAGES.baobabs,
    title: 'Tarangire Overnight Escape',
    duration: '2–3 days',
    tag: 'Short safari',
    text: 'Stay inside or near the park for dawn and dusk drives among elephant herds and ancient baobab landscapes.',
    highlights: ['Park lodge / camp', 'Sunrise drives', 'River wildlife'],
  },
  {
    image: TARANGIRE_IMAGES.wildlife,
    title: 'Tarangire & Manyara Combo',
    duration: '3–4 days',
    tag: 'Classic combo',
    text: 'Pair Tarangire\'s elephants and baobabs with Lake Manyara\'s groundwater forest and Rift Valley views.',
    highlights: ['Two parks', 'Escarpment views', 'Birdlife focus'],
  },
  {
    image: TARANGIRE_IMAGES.savanna,
    title: 'Northern Circuit Opener',
    duration: '5–7 days',
    tag: 'Most popular',
    text: 'Begin in Tarangire, then continue to Lake Manyara, Ngorongoro Crater, and the Serengeti  the classic northern route.',
    highlights: ['Multi-park itinerary', 'Private 4×4', 'Flexible lodges'],
  },
];

const GETTING_THERE_ROUTES = [
  'Drive from Arusha (approx. 2–3 hrs)  a popular day trip or Northern Circuit opener',
  'Combine with Lake Manyara, Ngorongoro, and Serengeti on a classic multi-park itinerary',
  'Fly into Kilimanjaro International Airport (JRO), then road transfer via Arusha',
  'Plan at least one full day dry-season wildlife concentrates along the Tarangire River',
];

function Lightbox({ image, onClose }) {
  if (!image) return null;

  const handleBackdropClick = (e) => {
    if (e.target === e.currentTarget) onClose();
  };

  return (
    <div
      className="tarangire-lightbox"
      role="dialog"
      aria-modal="true"
      aria-label="Image viewer"
      onClick={handleBackdropClick}
    >
      <button
        className="tarangire-lightbox__close"
        aria-label="Close image viewer"
        onClick={onClose}
      >
        ✕
      </button>
      <img
        className="tarangire-lightbox__img"
        src={image.src}
        alt={image.caption}
      />
      <span className="tarangire-lightbox__caption">{image.caption}</span>
    </div>
  );
}

function Tarangire() {
  const [lightboxImage, setLightboxImage] = useState(null);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <div className="tarangire">
      <section className="tarangire-hero" aria-labelledby="tarangire-hero-heading">
        <div className="tarangire-hero__media" aria-hidden="true">
          <HeroSlideshow
            classPrefix="tarangire-hero"
            slides={TARANGIRE_HERO_SLIDES}
            fallback={IMAGE_FALLBACK}
          />
          <div className="tarangire-hero__overlay" />
        </div>
        <div className="page-container tarangire-hero__container">
          <div className="tarangire-hero__content">
            <nav className="tarangire-hero__breadcrumb" aria-label="Breadcrumb">
              <a href="/">Home</a>
              <span className="tarangire-hero__breadcrumb-sep" aria-hidden="true">›</span>
              <a href="#tanzania-mainland">Mainland</a>
              <span className="tarangire-hero__breadcrumb-sep" aria-hidden="true">›</span>
              <span>Tarangire</span>
            </nav>
            <p className="tarangire-hero__eyebrow">Northern Safari Circuit</p>
            <h1 id="tarangire-hero-heading">
              Tarangire
              <span>National Park</span>
            </h1>
            <p className="tarangire-hero__lead">
              Land of giants  ancient baobabs, vast elephant herds, and
              riverine wilderness at the heart of Tanzania&apos;s Northern Circuit.
            </p>
            <div className="tarangire-hero__actions">
              <a className="tarangire-btn tarangire-btn--primary" href="#contact">
                Plan your Tarangire safari
              </a>
              <a className="tarangire-btn tarangire-btn--outline" href="#tarangire-gallery">
                View gallery
              </a>
            </div>
          </div>
        </div>
        <div className="tarangire-hero__stats">
          {HERO_STATS.map((stat) => (
            <div
              key={stat.label}
              className="tarangire-hero__stat"
              style={{ '--stat-delay': `${stat.delay}ms` }}
            >
              <span className="tarangire-hero__stat-value">{stat.value}</span>
              <span className="tarangire-hero__stat-label">{stat.label}</span>
            </div>
          ))}
        </div>
      </section>

      <section
        id="tarangire-overview"
        className="tarangire-section tarangire-overview"
        aria-labelledby="overview-heading"
      >
        <div className="page-container">
          <div className="tarangire-overview__grid">
            <Reveal className="tarangire-overview__text">
              <p className="tarangire-section__eyebrow">About the park</p>
              <h2 id="overview-heading">Where elephants meet baobabs</h2>
              <p>
                Tarangire National Park lies south of Lake Manyara on the
                Northern Circuit, defined by the Tarangire River and a landscape
                dotted with towering baobab trees. In the dry season, wildlife
                from across the region migrates here  creating one of Africa&apos;s
                greatest concentrations of elephants.
              </p>
              <p>
                Often overlooked in favour of the Serengeti, Tarangire rewards
                visitors with uncrowded game drives, dramatic scenery, and
                exceptional birdlife. It pairs perfectly with Ngorongoro and
                Manyara for a complete northern safari without the longest drives.
              </p>
              <ul className="tarangire-overview__details">
                <Reveal as="li" className="tarangire-overview__detail" delay={0}>
                  <span className="tarangire-overview__detail-label">Location</span>
                  <span className="tarangire-overview__detail-value">Northern Tanzania</span>
                </Reveal>
                <Reveal as="li" className="tarangire-overview__detail" delay={80}>
                  <span className="tarangire-overview__detail-label">Park size</span>
                  <span className="tarangire-overview__detail-value">2,850 km²</span>
                </Reveal>
                <Reveal as="li" className="tarangire-overview__detail" delay={160}>
                  <span className="tarangire-overview__detail-label">Established</span>
                  <span className="tarangire-overview__detail-value">1970</span>
                </Reveal>
                <Reveal as="li" className="tarangire-overview__detail" delay={240}>
                  <span className="tarangire-overview__detail-label">Best for</span>
                  <span className="tarangire-overview__detail-value">Elephants &amp; baobabs</span>
                </Reveal>
              </ul>
            </Reveal>
            <Reveal className="tarangire-overview__image" variant="right" delay={200}>
              <SafeImage
                className="tarangire-overview__img"
                src={TARANGIRE_IMAGES.overview}
                alt="Zebras on the savanna in Tarangire National Park"
              />
              <span className="tarangire-overview__image-accent" aria-hidden="true" />
            </Reveal>
          </div>
        </div>
      </section>

      {/* ──── Safari packages ──── */}
      <section
        id="tarangire-packages"
        className="tarangire-section tarangire-packages"
        aria-labelledby="packages-heading"
      >
        <div className="page-container">
          <Reveal className="tarangire-section__header">
            <p className="tarangire-section__eyebrow">Safari packages</p>
            <h2 id="packages-heading">Tarangire safari packages</h2>
            <p className="tarangire-section__intro">
              From a day trip out of Arusha to a full Northern Circuit journey 
              each package is tailored to your dates, lodge style, and travel pace.
            </p>
          </Reveal>
          <ul className="tarangire-packages__grid">
            {SAFARI_PACKAGES.map((pkg, index) => (
              <Reveal
                key={pkg.title}
                as="li"
                className="tarangire-packages__card"
                delay={index * 100}
              >
                <div className="tarangire-packages__card-image">
                  <SafeImage src={pkg.image} alt={pkg.title} />
                  <span className="tarangire-packages__duration">{pkg.duration}</span>
                </div>
                <div className="tarangire-packages__card-body">
                  <span className="tarangire-packages__tag">{pkg.tag}</span>
                  <h3>{pkg.title}</h3>
                  <p>{pkg.text}</p>
                  <ul className="tarangire-packages__highlights">
                    {pkg.highlights.map((item) => (
                      <li key={item}>{item}</li>
                    ))}
                  </ul>
                  <a className="tarangire-packages__cta" href="#contact">
                    Enquire about this package →
                  </a>
                </div>
              </Reveal>
            ))}
          </ul>
        </div>
      </section>

      <section
        id="tarangire-wildlife"
        className="tarangire-section tarangire-wildlife"
        aria-labelledby="wildlife-heading"
      >
        <div className="page-container">
          <Reveal className="tarangire-section__header">
            <p className="tarangire-section__eyebrow">Iconic wildlife</p>
            <h2 id="wildlife-heading">Life along the river</h2>
            <p className="tarangire-section__intro">
              From elephant super-herds to tree-dwelling pythons  Tarangire&apos;s
              river corridor sustains an extraordinary diversity of wildlife.
            </p>
          </Reveal>
          <ul className="tarangire-wildlife__grid">
            {WILDLIFE.map((item, index) => (
              <Reveal
                key={item.title}
                as="li"
                className="tarangire-wildlife__card"
                delay={index * 100}
                variant="scale"
              >
                <div className="tarangire-wildlife__icon-frame">
                  <span className="tarangire-wildlife__icon-ring" aria-hidden="true" />
                  <span
                    className="tarangire-wildlife__icon"
                    style={{ '--wildlife-face-position': item.facePosition }}
                  >
                    <img src={item.animalFace} alt={item.title} />
                  </span>
                </div>
                <h3>{item.title}</h3>
                <p>{item.text}</p>
              </Reveal>
            ))}
          </ul>
        </div>
      </section>

      <section
        id="tarangire-gallery"
        className="tarangire-section tarangire-gallery"
        aria-labelledby="gallery-heading"
      >
        <div className="page-container">
          <Reveal className="tarangire-section__header">
            <p className="tarangire-section__eyebrow">Photo gallery</p>
            <h2 id="gallery-heading">Scenes from Tarangire</h2>
            <p className="tarangire-section__intro">
              From riverbank elephant parades to baobab sunsets the quiet
              giant of the Northern Circuit in every season.
            </p>
          </Reveal>
          <div className="tarangire-gallery__grid">
            {GALLERY_IMAGES.map((img, index) => (
              <Reveal
                key={img.caption}
                className="tarangire-gallery__item"
                delay={index * 100}
                variant="scale"
                onClick={() => setLightboxImage(img)}
                role="button"
                tabIndex={0}
                aria-label={`View ${img.caption}`}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault();
                    setLightboxImage(img);
                  }
                }}
              >
                <SafeImage src={img.src} alt={img.caption} />
                <span className="tarangire-gallery__caption">{img.caption}</span>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {lightboxImage && (
        <Lightbox image={lightboxImage} onClose={() => setLightboxImage(null)} />
      )}

      <section
        id="tarangire-seasons"
        className="tarangire-section tarangire-seasons"
        aria-labelledby="seasons-heading"
      >
        <div className="page-container">
          <Reveal className="tarangire-section__header">
            <p className="tarangire-section__eyebrow">Plan your timing</p>
            <h2 id="seasons-heading">Best time to visit Tarangire</h2>
            <p className="tarangire-section__intro">
              Tarangire shines brightest in the dry season  but every month
              offers baobab drama, birdlife, and uncrowded game drives.
            </p>
          </Reveal>
          <ul className="tarangire-seasons__grid">
            {SEASONS.map((season, index) => (
              <Reveal
                key={season.title}
                as="li"
                className="tarangire-seasons__card"
                delay={index * 120}
              >
                <span
                  className={`tarangire-seasons__card-badge tarangire-seasons__card-badge--${season.badge}`}
                >
                  {season.badge === 'peak'
                    ? '★ Peak Season'
                    : season.badge === 'green'
                      ? '🌿 Green Season'
                      : '◐ Shoulder Season'}
                </span>
                <h3>{season.title}</h3>
                <span className="tarangire-seasons__card-months">{season.months}</span>
                <p>{season.text}</p>
              </Reveal>
            ))}
          </ul>
        </div>
      </section>

      <section
        id="tarangire-experiences"
        className="tarangire-section tarangire-experiences"
        aria-labelledby="experiences-heading"
      >
        <div className="page-container">
          <Reveal className="tarangire-section__header">
            <p className="tarangire-section__eyebrow">Safari experiences</p>
            <h2 id="experiences-heading">Ways to explore Tarangire</h2>
            <p className="tarangire-section__intro">
              From river-focused game drives to full Northern Circuit routes 
              Tarangire adds depth and elephants to any Tanzania itinerary.
            </p>
          </Reveal>
          <ul className="tarangire-experiences__grid">
            {EXPERIENCES.map((exp, index) => (
              <Reveal
                key={exp.title}
                as="li"
                className="tarangire-experiences__card"
                delay={index * 120}
              >
                <div className="tarangire-experiences__card-image">
                  <SafeImage src={exp.image} alt={exp.title} />
                </div>
                <div className="tarangire-experiences__card-body">
                  <h3>{exp.title}</h3>
                  <p>{exp.text}</p>
                </div>
              </Reveal>
            ))}
          </ul>
        </div>
      </section>

      <section
        id="tarangire-getting-there"
        className="tarangire-section tarangire-getting-there"
        aria-labelledby="getting-there-heading"
      >
        <div className="page-container">
          <div className="tarangire-getting-there__inner">
            <Reveal className="tarangire-getting-there__copy">
              <p className="tarangire-section__eyebrow">Getting there</p>
              <h2 id="getting-there-heading">How to reach Tarangire</h2>
              <p>
                Tarangire sits between Arusha and Lake Manyara on the road to
                the southern Serengeti. Most Northern Circuit safaris include
                at least one day here especially during the dry season.
              </p>
              <ul className="tarangire-getting-there__routes">
                {GETTING_THERE_ROUTES.map((route) => (
                  <li key={route}>{route}</li>
                ))}
              </ul>
            </Reveal>
            <Reveal className="tarangire-getting-there__map" variant="right" delay={200}>
              <iframe
                title="Tarangire National Park location"
                src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d255168.089654321!2d36.0!3d-3.833!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x1837134567890abc%3A0xabcdef1234567890!2sTarangire%20National%20Park!5e0!3m2!1sen!2stz!4v1700000000000!5m2!1sen!2stz"
                loading="lazy"
                referrerPolicy="no-referrer-when-downgrade"
              />
            </Reveal>
          </div>
        </div>
      </section>

      <section className="tarangire-cta" aria-labelledby="tarangire-cta-heading">
        <div className="page-container">
          <Reveal variant="scale" delay={0}>
            <div className="tarangire-cta__inner">
              <h2 id="tarangire-cta-heading">
                Ready to walk among the giants?
              </h2>
              <p>
                Tell us your travel dates we&apos;ll design a tailor-made
                Tarangire safari with river drives, baobab lodges, and Northern
                Circuit connections.
              </p>
              <a
                className="tarangire-btn tarangire-btn--primary tarangire-btn--lg"
                href="#contact"
              >
                Get a free Tarangire quote
              </a>
            </div>
          </Reveal>
        </div>
      </section>
    </div>
  );
}

export default Tarangire;
